import {
  DEFAULT_WIDGET_LAYOUT_SETTINGS,
  Widget,
  WidgetLayoutMode,
  WidgetLayoutSettings,
} from '@neptune/shared/widgets-domain';

import { isSectionVariantWidget, SectionVariant } from 'domain/widget';

const hasSameValue = <T>(value1: T | undefined, value2: T | undefined) =>
  value1 === undefined || value2 === undefined || value1 === value2;

const hasSameLayoutModes = (
  layoutSettings1: WidgetLayoutSettings,
  layoutSettings2: WidgetLayoutSettings,
) => hasSameValue(layoutSettings1.mode, layoutSettings2.mode);

const hasSameColumnCounts = (
  layoutSettings1: WidgetLayoutSettings,
  layoutSettings2: WidgetLayoutSettings,
) =>
  hasSameValue(
    layoutSettings1.config.gridMode.columnsCount,
    layoutSettings2.config.gridMode.columnsCount,
  );

export type LayoutSettingsComparison = {
  hasConflict: boolean;
  hasModeConflict: boolean;
  hasColumnCountsConflict: boolean;
};

export const getLayoutSettingsComparison = (
  layoutSettings1: WidgetLayoutSettings,
  layoutSettings2?: WidgetLayoutSettings,
): LayoutSettingsComparison => {
  if (!layoutSettings2) {
    return {
      hasConflict: false,
      hasModeConflict: false,
      hasColumnCountsConflict: false,
    };
  }

  const hasModeConflict = !hasSameLayoutModes(layoutSettings1, layoutSettings2);
  const hasBothLayoutsInCustomMode =
    layoutSettings1.mode === WidgetLayoutMode.Custom &&
    layoutSettings2.mode === WidgetLayoutMode.Custom;
  const hasAnyLayoutInCustomMode = [layoutSettings1.mode, layoutSettings2.mode].includes(
    WidgetLayoutMode.Custom,
  );
  const hasColumnCountsConflict =
    !hasBothLayoutsInCustomMode && !hasSameColumnCounts(layoutSettings1, layoutSettings2);

  return {
    hasConflict: hasModeConflict || (!hasAnyLayoutInCustomMode && hasColumnCountsConflict),
    hasModeConflict,
    hasColumnCountsConflict,
  };
};

export const hasConflictInLayoutSettings = (
  layoutSettings1: WidgetLayoutSettings,
  layoutSettings2?: WidgetLayoutSettings,
) => getLayoutSettingsComparison(layoutSettings1, layoutSettings2).hasConflict;

export const hasConflictWithDefaultSettings = (layoutSettings: WidgetLayoutSettings) =>
  hasConflictInLayoutSettings(layoutSettings, DEFAULT_WIDGET_LAYOUT_SETTINGS);

export const countSectionOverrides = ({
  widgetLayoutSettings,
  globalLayoutSettings,
}: {
  globalLayoutSettings: WidgetLayoutSettings;
  widgetLayoutSettings: WidgetLayoutSettings[];
}) =>
  widgetLayoutSettings.reduce(
    (sum, layoutSettings) =>
      sum + (hasConflictInLayoutSettings(layoutSettings, globalLayoutSettings) ? 1 : 0),
    0,
  );

export const shouldResetLayout = (
  widget: Widget,
  globalLayoutSettings: WidgetLayoutSettings,
): widget is SectionVariant =>
  isSectionVariantWidget(widget) &&
  hasConflictInLayoutSettings(globalLayoutSettings, widget.options?.layoutSettings);

export const resetWidgetLayout = (widget: SectionVariant): Widget => ({
  ...widget,
  options: {
    ...widget.options,
    layoutSettings: undefined,
  },
});
